import React from "react";
import PropTypes from "prop-types";
import { sanitizeImageUrl } from "../../../Utils/imageUtils";

import { useTranslation } from "react-i18next";

/**
 * CardHeader - Header section with image and overlay elements
 * Supports badges, buttons, and other overlay elements as children
 * 
 * @component
 * @example
 * <CardHeader
 *   image="https://example.com/image.jpg"
 *   alt="Product Image"
 *   onImageClick={handleClick}
 *   onImageLoad={() => setImageLoaded(true)}
 *   imageLoaded={true}
 * >
 *   <BadgeOverlay position="top-right">
 *     <Badge>New</Badge>
 *   </BadgeOverlay>
 * </CardHeader>
 */
const CardHeader = ({
    image,
    alt,
    onImageClick,
    onImageLoad,
    onImageError,
    imageLoaded = false,
    children,
    className = "",
    imageClassName = "",
    imageStyle = {},
}) => {
    const { t } = useTranslation();
    const defaultAlt = t("career.cardImage");
    return (
        <div className={`relative flex justify-center rounded-t-[20px] ${className}`}>
            <img
                src={sanitizeImageUrl(image)}
                alt={alt || defaultAlt}
                className={`rounded-t-[20px] w-full aspect-square object-cover hover:cursor-pointer transition-transform duration-300 ease-in-out opacity-90 ${imageClassName}`}
                onClick={onImageClick}
                onLoad={onImageLoad}
                onError={onImageError}
                style={imageStyle}
            />
            {imageLoaded && children}
        </div>
    );
};

CardHeader.propTypes = {
    image: PropTypes.string.isRequired,
    alt: PropTypes.string,
    onImageClick: PropTypes.func,
    onImageLoad: PropTypes.func,
    onImageError: PropTypes.func,
    imageLoaded: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
};

export default CardHeader;
